import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Text, Center, Loader } from "@mantine/core";
import { IconFileText, IconPencil, IconTrash } from "@tabler/icons-react";
import { toast } from "sonner";
import type { CustomerNoteHistory } from "../api/queries/customerRepresentativeQueries";
import {
  useAddPaymentNoteMutation,
  useDeletePaymentNoteMutation,
  usePaymentNotesHistoryQuery,
  useUpdatePaymentNoteMutation,
} from "../api/queries/paymentsQueries";
import { useAuth } from "../hooks/useAuth";
import { displayISODate } from "../utils/display";
import { ConfirmationModal } from "./ConfirmationModal";
import { CollapsibleHistoryCard } from "./CollapsibleHistoryCard";
import { CustomerNoteBody } from "./CustomersTable/CustomerNoteBody";
import { MentionTextarea } from "./CustomersTable/MentionTextarea";
import { CustomerNoteAttachmentList } from "./CustomersTable/CustomerNoteAttachmentList";
import { CustomerNoteAttachmentPicker } from "./CustomersTable/CustomerNoteAttachmentPicker";

type InvoiceNotesHistorySectionProps = {
  invoiceId: string;
  customerOrgId: number;
  defaultHistoryOpen?: boolean;
  queriesEnabled?: boolean;
};

export function InvoiceNotesHistorySection({
  invoiceId,
  customerOrgId,
  defaultHistoryOpen = false,
  queriesEnabled = true,
}: InvoiceNotesHistorySectionProps) {
  const [historyOpen, setHistoryOpen] = useState(defaultHistoryOpen);
  const [noteValue, setNoteValue] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [searchParams] = useSearchParams();
  const highlightNoteId = Number(searchParams.get("noteId")) || null;

  const { uid, canMutateOrg } = useAuth();
  const currentUserId = uid ? Number(uid) : null;

  const { data: notes = [], isLoading: isHistoryLoading } =
    usePaymentNotesHistoryQuery(invoiceId, { enabled: queriesEnabled });
  const addNoteMutation = useAddPaymentNoteMutation();
  const updateNoteMutation = useUpdatePaymentNoteMutation();
  const deleteNoteMutation = useDeletePaymentNoteMutation();

  useEffect(() => {
    if (highlightNoteId) {
      setHistoryOpen(true);
    }
  }, [highlightNoteId]);

  const handleAddNote = async () => {
    const trimmed = noteValue.trim();
    if (!trimmed && selectedFiles.length === 0) return;
    try {
      await addNoteMutation.mutateAsync({
        paymentExternalId: invoiceId,
        body: trimmed || undefined,
        files: selectedFiles,
      });
      setNoteValue("");
      setSelectedFiles([]);
    } catch (error) {
      console.error("Error adding invoice note:", error);
    }
  };

  const isSaving =
    addNoteMutation.isPending ||
    updateNoteMutation.isPending ||
    deleteNoteMutation.isPending;
  const canSubmitNote = Boolean(noteValue.trim()) || selectedFiles.length > 0;

  return (
    <div className="w-full space-y-3">
      <CollapsibleHistoryCard
        icon={IconFileText}
        label="Notes"
        count={notes.length}
        isLoading={isHistoryLoading}
        open={historyOpen}
        onToggle={() => setHistoryOpen(!historyOpen)}
      >
        {isHistoryLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : notes.length === 0 ? (
          <Text c="dimmed" size="sm" ta="center" py="md">
            No notes yet.
          </Text>
        ) : (
          <div className="space-y-4">
            {notes.map((entry) => (
              <InvoiceNoteRow
                key={entry.id}
                entry={entry}
                customerOrgId={customerOrgId}
                highlight={highlightNoteId === entry.id}
                canModify={
                  canMutateOrg &&
                  currentUserId !== null &&
                  entry.author_id !== null &&
                  entry.author_id === currentUserId
                }
                isMutating={isSaving}
                onUpdateNote={(noteId, body) =>
                  updateNoteMutation.mutateAsync({
                    paymentExternalId: invoiceId,
                    noteId,
                    body,
                  })
                }
                onDeleteNote={(noteId) =>
                  deleteNoteMutation.mutateAsync({
                    paymentExternalId: invoiceId,
                    noteId,
                  })
                }
              />
            ))}
          </div>
        )}
      </CollapsibleHistoryCard>

      {canMutateOrg ? (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Add note
          </p>
          <MentionTextarea
            placeholder="Add a note…"
            value={noteValue}
            onChange={setNoteValue}
            rows={2}
            className="w-full resize-y rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-gray-700 outline-none focus:border-brand focus:ring-2 focus:ring-brand-subtle"
          />
          <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
            <CustomerNoteAttachmentPicker
              files={selectedFiles}
              onChange={setSelectedFiles}
              disabled={isSaving}
            />
            <button
              type="button"
              onClick={handleAddNote}
              disabled={isSaving || !canSubmitNote}
              className="shrink-0 cursor-pointer rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {addNoteMutation.isPending ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

type InvoiceNoteRowProps = {
  entry: CustomerNoteHistory;
  customerOrgId: number;
  highlight: boolean;
  canModify: boolean;
  isMutating: boolean;
  onUpdateNote: (noteId: number, body: string) => Promise<unknown>;
  onDeleteNote: (noteId: number) => Promise<unknown>;
};

function InvoiceNoteRow({
  entry,
  customerOrgId,
  highlight,
  canModify,
  isMutating,
  onUpdateNote,
  onDeleteNote,
}: InvoiceNoteRowProps) {
  const itemRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(entry.body);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const author = entry.author_name?.trim() || "Unknown";

  useEffect(() => {
    if (highlight && itemRef.current) {
      itemRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlight]);

  const handleSaveEdit = async () => {
    const trimmed = editValue.trim();
    if (!trimmed) return;
    try {
      await onUpdateNote(entry.id, trimmed);
      setIsEditing(false);
    } catch {
      toast.error("Failed to update note");
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await onDeleteNote(entry.id);
      setDeleteConfirmOpen(false);
    } catch {
      toast.error("Failed to delete note");
    }
  };

  return (
    <div
      ref={itemRef}
      className={`rounded-md ${highlight ? "bg-blue-50 ring-1 ring-blue-200" : ""}`}
    >
      {isEditing ? (
        <div className="space-y-2" onClick={(event) => event.stopPropagation()}>
          <MentionTextarea
            value={editValue}
            onChange={setEditValue}
            rows={2}
            className="w-full resize-y rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-gray-700 outline-none focus:border-brand focus:ring-2 focus:ring-brand-subtle"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setEditValue(entry.body);
                setIsEditing(false);
              }}
              disabled={isMutating}
              className="cursor-pointer rounded-md border border-slate-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-slate-100 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveEdit}
              disabled={isMutating || !editValue.trim()}
              className="cursor-pointer rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
            >
              Save
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <Text className="font-semibold capitalize text-gray-800 text-base">
              {author}
            </Text>
            <Text
              c="dimmed"
              size="xs"
              mt={4}
              mb={entry.body.trim() ? 8 : 0}
              className="italic text-gray-500"
            >
              {displayISODate(entry.created_at)}
            </Text>
            {entry.body.trim() ? (
              <Text
                size="sm"
                className="text-gray-700"
                style={{ whiteSpace: "pre-wrap" }}
              >
                <CustomerNoteBody body={entry.body} className="text-gray-700" />
              </Text>
            ) : null}
            <CustomerNoteAttachmentList
              customerOrgId={customerOrgId}
              noteId={entry.id}
              attachments={entry.attachments ?? []}
            />
          </div>
          {canModify ? (
            <span
              className="flex shrink-0"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                title="Edit note"
                aria-label="Edit note"
                onClick={() => {
                  setEditValue(entry.body);
                  setIsEditing(true);
                }}
                disabled={isMutating}
                className="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-gray-400 hover:bg-slate-100 hover:text-gray-700 disabled:opacity-60"
              >
                <IconPencil size={14} />
              </button>
              <button
                type="button"
                title="Delete note"
                aria-label="Delete note"
                onClick={() => setDeleteConfirmOpen(true)}
                disabled={isMutating}
                className="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-60"
              >
                <IconTrash size={14} />
              </button>
            </span>
          ) : null}
        </div>
      )}
      <ConfirmationModal
        open={deleteConfirmOpen}
        title="Delete this note?"
        description="This note will be removed from the invoice history. This action cannot be undone."
        confirmLabel="Delete note"
        isLoading={isMutating}
        onCancel={() => {
          if (!isMutating) setDeleteConfirmOpen(false);
        }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
